import { useQuery } from '@apollo/client';
import { useCallback } from 'react';

import { GET_EARN_POINTS_INFO } from '../../graphql/profile/queries';
import { EarnPointsInfoResponse } from '../../graphql/profile/models';
import { getCvRewardPoints } from '../../utils/rewards';
import { isCvDisplayPeriodActive } from '../../utils/cv/is-cv-display-period-active';
import { CvTerms, setCvTerms, AppointmentType } from '../../graphql';

export interface CvBonusInfo {
  cvBonusPoints: number;
  cvBonusEndDate: string;
  fromDays: number;
  toDays: number;
  code: string;
  isCvPeriodActive: boolean;
  isLoading: boolean;
}

const getCvAvailableDates = (dataEarn?: EarnPointsInfoResponse) => dataEarn?.earnPointsInfo?.appointments
  ?.availableDates?.find((availableDate) => availableDate.appointmentType === AppointmentType.controlVisit);

export const useCvBonusInfo = (isEnabled: boolean): CvBonusInfo => {
  const getValues = useCallback((dataEarn?: EarnPointsInfoResponse): CvTerms => {
    const cvAvailableDates = getCvAvailableDates(dataEarn);

    return {
      points: getCvRewardPoints(dataEarn?.rewardList) ?? 0,
      fromDays: dataEarn?.profile?.controlVisitDateRange?.fromDays ?? 0,
      toDays: dataEarn?.profile?.controlVisitDateRange?.toDays ?? 0,
      startDate: cvAvailableDates?.startDate ?? '',
      endDate: cvAvailableDates?.endDate ?? '',
    };
  }, [getCvRewardPoints]);
  const setValue = useCallback((dataEarn?: EarnPointsInfoResponse) => {
    if (dataEarn) {
      setCvTerms(getValues(dataEarn));
    }
  }, [setCvTerms, getValues]);
  const { data, loading } = useQuery<EarnPointsInfoResponse>(GET_EARN_POINTS_INFO, {
    skip: !isEnabled,
    onCompleted: setValue,
  });

  const code = data?.profile?.groupLECodeForFirstFitting ?? '';

  const cvBonusEndDate = getCvAvailableDates(data)?.endDate ?? '';
  const isCvPeriodActive = isCvDisplayPeriodActive(cvBonusEndDate);
  const { points: cvBonusPoints, toDays, fromDays } = getValues(data);

  return { cvBonusPoints, cvBonusEndDate, fromDays, toDays, code, isCvPeriodActive, isLoading: loading };
};
